/**
 * @fileoverview Header card of the student tracker: project name (or the
 * generic "Project assessments" title inside the project view) and the
 * done/total totalizers of the prototype.
 */

import { Anchor, Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { StudentTrackerStat, StudentTrackerSummary as Summary } from '../types'

interface StudentTrackerSummaryProps {
  summary: Summary
  /** Shown with a "View project" link on the standalone page; omitted inside the project view. */
  projectId?: string
  projectName?: string
  projectCompleted?: boolean
  isMobile: boolean
}

const PROJECT_NAME_MAX_WIDTH = 320
const LINK_ICON_SIZE = 12
const ITEM_STYLE = { wordBreak: 'break-word' as const, minWidth: 0 }

export function StudentTrackerSummary({
  summary,
  projectId,
  projectName,
  projectCompleted = false,
  isMobile,
}: StudentTrackerSummaryProps) {
  const { t } = useTranslation()

  const items = (
    [
      { key: 'deliverables', stat: summary.deliverables },
      { key: 'self_assessments', stat: summary.selfAssessments },
      { key: 'teacher_assessments', stat: summary.teacherAssessments },
    ] as Array<{ key: string; stat: StudentTrackerStat }>
  ).filter(item => item.stat.total > 0)

  if (items.length === 0) return null

  const header = (
    <Group spacing='sm' align='center' noWrap={!isMobile} sx={{ minWidth: 0 }}>
      <Text size='sm' weight={600} lineClamp={2} sx={{ maxWidth: isMobile ? undefined : PROJECT_NAME_MAX_WIDTH }}>
        {projectName ?? t('student_task_tracker.summary.title')}
      </Text>
      {projectCompleted && (
        <Badge color='green' variant='light' radius='sm' sx={{ flexShrink: 0 }}>
          {t('student_task_tracker.summary.completed')}
        </Badge>
      )}
      {projectId && (
        <Anchor
          href={`/projects/${projectId}`}
          target='_blank'
          rel='noopener noreferrer'
          size='xs'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
        >
          {t('student_task_tracker.summary.view_project')}
          <IconExternalLink size={LINK_ICON_SIZE} />
        </Anchor>
      )}
    </Group>
  )

  const stats = items.map(({ key, stat }) => {
    const allDone = stat.done === stat.total
    return (
      <Text key={key} size='sm' color='dimmed' style={ITEM_STYLE}>
        {t(`student_task_tracker.summary.${key}`)}:{' '}
        <Text span weight={600} color={allDone ? 'teal' : 'dark'}>
          {stat.done}/{stat.total}
        </Text>
      </Text>
    )
  })

  return (
    <Paper withBorder radius='md' p='md'>
      {isMobile ? (
        <Stack spacing='sm'>
          {header}
          <Stack spacing={4}>{stats}</Stack>
        </Stack>
      ) : (
        <Group position='apart' align='center' noWrap>
          {header}
          <Group spacing='xl' noWrap sx={{ minWidth: 0 }}>
            {stats}
          </Group>
        </Group>
      )}
    </Paper>
  )
}
